import { PublicClientApplication } from '@azure/msal-browser';
import { MsalProvider } from '@azure/msal-react';
import { Backdrop, CircularProgress } from '@mui/material';
import { GoogleOAuthProvider } from '@react-oauth/google';
import isEqual from 'lodash.isequal';
import { useCallback, useEffect, useMemo, useState } from 'react';

import Logger from 'browser-logging/src/browser/Logger';

import {
  hasAdminAccess,
  hasClientSystemAdminAccess,
  hasOrgSwitchingAccess,
  hasReadOnlyAccess,
  hasSystemAdminAccess,
} from 'common/src/authorization/accessChecks';
import { FeatureChecker } from 'common/src/features/FeatureChecker';
import { DevFeatures } from 'common/src/types/enums/ProductFeatures.enum';
import { IAuthenticatedUser, IOrganization } from 'common/src/types/interfaces';

import { API_BASE_PATHS } from 'portal/src/api/constants';
import { MESSAGES } from 'portal/src/constants/snackbarMessages';
import { getLayoutVersion } from 'portal/src/layouts/LayoutPreference/layoutPreferenceUtils';

import AuthContext, { AuthContextType } from './AuthContext';
import {
  getProfilePictureFromStorage,
  getUserFromStorage,
  setProfilePictureInStorage,
  setUserInStorage,
  setUserReloading,
} from './UserUtils';
import { STORAGE_KEYS } from './storageKeys';

import { getProfilePicture } from '../api/UsersService';
import useSnackbarHandler from '../utils/Snackbar/useSnackbarHandler';

const API_URL = import.meta.env.VITE_API_URL;
const googleOAuthClientId = import.meta.env.VITE_GOOGLE_CLIENT_ID;
const msalInstance = new PublicClientApplication({
  auth: {
    clientId: import.meta.env.VITE_MICROSOFT_CLIENT_ID,
    redirectUri: import.meta.env.VITE_MICROSOFT_REDIRECT_URI,
  },
});

// The API responds with { redirect } instead of the user when the user's
// organization belongs to another product's portal.
const redirectToProduct = (data: { redirect?: string }): boolean => {
  if (!data.redirect) {
    return false;
  }
  window.location.href = data.redirect;
  return true;
};

export default function AuthProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const snackbarHandler = useSnackbarHandler();
  const [showBackdrop, setShowBackdrop] = useState(false);
  const [localUser, setLocalUser] = useState<IAuthenticatedUser | undefined>(
    getUserFromStorage(),
  );
  const [profilePicture, setProfilePicture] = useState(
    getProfilePictureFromStorage(),
  );

  useEffect(() => {
    let handleStorageChange;
    setUserReloading('false');
    const syncUser = async () => {
      if (localUser) {
        await refreshUser();
        handleStorageChange = (event: StorageEvent) => {
          if (event.key === STORAGE_KEYS.USER) {
            const { organizationId: orgId, profilePicture: oldPicture } =
              JSON.parse(event.oldValue);
            const { organizationId: newOrgId, profilePicture: newPicture } =
              JSON.parse(event.newValue);

            if (orgId === newOrgId && oldPicture === newPicture) {
              return;
            }

            window.location.reload();
          }
        };

        window.addEventListener('storage', handleStorageChange);
      } else {
        await restoreSessionFromCookie();
      }
    };

    syncUser();

    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  });

  const featureChecker = useMemo(
    () =>
      new FeatureChecker(
        import.meta.env.VITE_DEPLOY_ENV?.toLowerCase(),
        (localUser?.organization ?? {}) as IOrganization,
      ),
    [localUser],
  );

  const workosLoginEnabled = featureChecker.devFeatureEnabled(
    DevFeatures.WorkOsLogin,
  );

  const fetchProfilePicture = useCallback(
    async (userId: string): Promise<any> => {
      try {
        const fileReader = new FileReader();
        const src = await getProfilePicture(userId);

        fileReader.onload = (evt) => {
          const url = evt.target.result;

          setProfilePictureInStorage(url);
          setProfilePicture(url as string);
        };

        fileReader.readAsDataURL(src);

        return true;
      } catch (err) {
        setProfilePictureInStorage(null);
        setProfilePicture(null);
        Logger.captureError(err as Error);
      }
    },
    [],
  );

  const setUser = useCallback(
    async (user: IAuthenticatedUser | undefined) => {
      const pictureChanged = localUser?.profilePicture !== user?.profilePicture;
      // If user is defined and localUser is not, this is a login so update localUser
      // If user is undefined and localUser is defined, this is a logout so update localUser
      // If both user and localUser are defined and are not equal, this is an org switch or a token refresh so update the user
      if (
        (user && !localUser) ||
        (!user && localUser) ||
        (user && localUser && !isEqual(user, localUser))
      ) {
        setUserInStorage(user);
        setLocalUser(user);
      }

      if (
        user?.id &&
        user?.profilePicture &&
        (!profilePicture || pictureChanged)
      ) {
        await fetchProfilePicture(user.id);
      } else if (!user?.id || !user?.profilePicture) {
        setProfilePictureInStorage(null);
        setProfilePicture(null);
      }

      if (
        window.pendo &&
        user?.id &&
        user?.email &&
        user?.name &&
        user?.userType &&
        user?.organizationId
      ) {
        pendo.initialize({
          visitor: {
            id: user.id,
            email: user.email,
            name: user.name,
            userPermissions: user.userType,
            layoutVersion: getLayoutVersion(),
          },
          account: {
            id: user.organizationId,
            name: user.organization?.name,
          },
        });
      }
    },
    [fetchProfilePicture, localUser, profilePicture],
  );

  const restoreSessionFromCookie = useCallback(async (): Promise<void> => {
    try {
      const response = await fetch(
        `${API_URL}${API_BASE_PATHS.AUTH.RESTORE_SESSION}`,
        {
          credentials: 'include',
        },
      );
      if (!response.ok) {
        return;
      }

      const user = await response.json();
      if (redirectToProduct(user)) {
        return;
      }
      setUser(user as IAuthenticatedUser);
    } catch (err) {
      Logger.captureError(err as Error);
    }
  }, [setUser]);

  const handleLogin = useCallback(
    async (loginPath: string, authData: any): Promise<boolean> => {
      try {
        const response = await fetch(`${API_URL}${loginPath}`, {
          body: JSON.stringify(authData),
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
          },
          method: 'post',
        });

        if (!response.ok) {
          throw new Error('unable to login');
        }

        const data = await response.json();
        if (redirectToProduct(data)) {
          return true;
        }
        setUser(data as IAuthenticatedUser);
        return true;
      } catch (err) {
        Logger.captureError(err as Error);
        return false;
      }
    },
    [setUser],
  );

  const initiateEnterpriseSSOLogin = useCallback(
    async (email: string): Promise<string | null> => {
      try {
        const response = await fetch(
          `${API_URL}${API_BASE_PATHS.AUTH.WORKOS_INITIATE}`,
          {
            body: JSON.stringify({ email }),
            headers: { 'Content-Type': 'application/json' },
            method: 'post',
          },
        );
        if (!response.ok) return null;
        const { authorizationUrl } = await response.json();
        return authorizationUrl ?? null;
      } catch (err) {
        Logger.captureError(err as Error);
        return null;
      }
    },
    [],
  );

  const removeUser = useCallback(() => {
    setUser(undefined);
  }, [setUser]);

  const getUserPicture = useCallback(
    (): any => profilePicture,
    [profilePicture],
  );

  const getUser = useCallback((): IAuthenticatedUser | undefined => {
    if (localUser) {
      if (!localUser.organization) {
        localUser.organization = {} as IOrganization;
      }
    }

    return localUser;
  }, [localUser]);

  const changePassword = useCallback(
    async (newPassword: string): Promise<boolean> => {
      const route = workosLoginEnabled
        ? API_BASE_PATHS.AUTH.WORKOS_CHANGE_PASSWORD
        : API_BASE_PATHS.AUTH.CHANGE_PASSWORD;
      const response = await fetch(`${API_URL}${route}`, {
        body: JSON.stringify({ newPassword }),
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        method: 'post',
      });

      if (!response.ok) {
        throw new Error('unable to change the password');
      }

      return true;
    },
    [workosLoginEnabled],
  );

  const googleLogin = useCallback(
    async (idToken: string): Promise<boolean> => {
      return handleLogin(API_BASE_PATHS.AUTH.GOOGLE, { idToken });
    },
    [handleLogin],
  );

  const verifyWorkosEmail = useCallback(
    async (code: string): Promise<boolean> => {
      return handleLogin(API_BASE_PATHS.AUTH.WORKOS_VERIFY_EMAIL, { code });
    },
    [handleLogin],
  );

  const isAuthenticated = useCallback((): boolean => {
    return !!localUser;
  }, [localUser]);

  const isPasswordExpired = useCallback((): boolean => {
    return localUser?.passwordExpired ?? false;
  }, [localUser]);

  const isReadyForAuthorization = useCallback((): boolean => {
    return !!localUser?.userType;
  }, [localUser]);

  const login = useCallback(
    async (email: string, password: string): Promise<boolean> => {
      const route = workosLoginEnabled
        ? API_BASE_PATHS.AUTH.WORKOS_LOGIN
        : API_BASE_PATHS.AUTH.LOGIN;
      return handleLogin(route, { email, password });
    },
    [workosLoginEnabled, handleLogin],
  );

  const logout = useCallback(async () => {
    try {
      const route = workosLoginEnabled
        ? API_BASE_PATHS.AUTH.WORKOS_LOGOUT
        : API_BASE_PATHS.AUTH.LOGOUT;
      const response = await fetch(`${API_URL}${route}`, {
        credentials: 'include',
      });
      if (!response.ok) {
        throw new Error('something went wrong');
      }

      removeUser();
      return true;
    } catch (err) {
      Logger.captureError(err as Error);
      return false;
    }
  }, [workosLoginEnabled, removeUser]);

  const microsoftLogin = useCallback(
    async (accessToken: string): Promise<boolean> => {
      return handleLogin(API_BASE_PATHS.AUTH.MICROSOFT, { accessToken });
    },
    [handleLogin],
  );

  const refreshUser = useCallback(async (): Promise<any> => {
    try {
      const response = await fetch(
        `${API_URL}${API_BASE_PATHS.AUTH.REFRESH_USER_TOKEN}`,
        {
          body: JSON.stringify(getUserFromStorage()),
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json ',
          },
          method: 'post',
        },
      );

      const userUpdated = await response.json();
      if (redirectToProduct(userUpdated)) {
        return;
      }

      setUser(userUpdated);
    } catch (err) {
      Logger.captureError(err as Error);
    }
  }, [setUser]);

  const signup = useCallback(
    async (uid: string, password: string): Promise<boolean> => {
      try {
        const response = await fetch(
          `${API_URL}${API_BASE_PATHS.AUTH.SIGNUP}`,
          {
            body: JSON.stringify({ uid, password }),
            credentials: 'include',
            headers: {
              'Content-Type': 'application/json',
            },
            method: 'post',
          },
        );

        if (!response.ok) {
          throw new Error('unable to signup');
        }

        const data = await response.json();
        if (redirectToProduct(data)) {
          return true;
        }
        setUser(data as IAuthenticatedUser);
        return true;
      } catch (err) {
        Logger.captureError(err as Error);
        return false;
      }
    },
    [setUser],
  );

  const ssoAuthLogin = useCallback(
    async (jwt: string): Promise<boolean> => {
      try {
        const response = await fetch(
          `${API_URL}${API_BASE_PATHS.AUTH.SSO_LOGIN}`,
          {
            body: JSON.stringify({ jwt }),
            credentials: 'include',
            headers: {
              'Content-Type': 'application/json',
            },
            method: 'post',
          },
        );

        if (!response.ok) {
          throw new Error('unable to login');
        }

        const data = await response.json();
        if (redirectToProduct(data)) {
          return true;
        }
        setUser(data);
        return true;
      } catch (err) {
        Logger.captureError(err as Error);
        return false;
      }
    },
    [setUser],
  );

  const switchOrganization = useCallback(
    async (orgId: string): Promise<void> => {
      setShowBackdrop(true);
      try {
        const response = await fetch(
          `${API_URL}${API_BASE_PATHS.AUTH.SWITCH_ORGANIZATION}/${orgId}`,
          { credentials: 'include' },
        );

        if (!response.ok) {
          throw new Error('unable to refresh JWT');
        }

        const data = await response.json();
        if (redirectToProduct(data)) {
          return;
        }
        setUser(data as IAuthenticatedUser);

        setUserReloading();
        await window.location.reload();
      } catch (err) {
        setShowBackdrop(false);
        Logger.captureError(err as Error);
        snackbarHandler.error(MESSAGES.ORGANIZATION.SWITCH_ERROR);
      }
    },
    [setUser, snackbarHandler],
  );

  const tokenLogin = useCallback(
    async (jwt: string): Promise<boolean> => {
      try {
        const response = await fetch(
          `${API_URL}${API_BASE_PATHS.AUTH.TOKEN_LOGIN}?code=${jwt}`,
          {
            credentials: 'include',
            headers: {
              'Content-Type': 'application/json',
            },
            method: 'post',
          },
        );

        if (!response.ok) {
          throw new Error('unable to login');
        }

        const data = await response.json();
        if (redirectToProduct(data)) {
          return true;
        }
        setUser(data);
        return true;
      } catch (err) {
        Logger.captureError(err as Error);
        return false;
      }
    },
    [setUser],
  );

  const userHasAdminAccess = useCallback((): boolean => {
    return hasAdminAccess(localUser?.userType);
  }, [localUser]);

  const userHasClientSystemAdminAccess = useCallback((): boolean => {
    return hasClientSystemAdminAccess(localUser?.userType);
  }, [localUser]);

  const userHasOrgSwitchingAccess = useCallback((): boolean => {
    return hasOrgSwitchingAccess(localUser?.userType);
  }, [localUser]);

  const userHasSystemAdminAccess = useCallback((): boolean => {
    return hasSystemAdminAccess(localUser?.userType);
  }, [localUser]);

  const userHasReadOnlyAccess = useCallback((): boolean => {
    return hasReadOnlyAccess(localUser?.userType);
  }, [localUser]);

  const verifyUserAccountProvider = useCallback(async (): Promise<any> => {
    try {
      const response = await fetch(
        `${API_URL}${API_BASE_PATHS.AUTH.VERIFY_CURRENT_USER_ACCOUNT_PROVIDER}`,
        {
          credentials: 'include',
          method: 'get',
        },
      );

      if (!response.ok) {
        throw new Error('unable to verify the current user');
      }

      const data = await response.json();
      return data;
    } catch (err) {
      Logger.captureError(err as Error);
      return false;
    }
  }, []);

  const value: AuthContextType = {
    featureChecker,
    changePassword,
    getUser,
    getUserPicture,
    googleLogin,
    hasAdminAccess: userHasAdminAccess,
    hasClientSystemAdminAccess: userHasClientSystemAdminAccess,
    hasOrgSwitchingAccess: userHasOrgSwitchingAccess,
    hasReadOnlyAccess: userHasReadOnlyAccess,
    hasSystemAdminAccess: userHasSystemAdminAccess,
    initiateEnterpriseSSOLogin,
    isAuthenticated,
    isPasswordExpired,
    isReadyForAuthorization,
    login,
    logout,
    microsoftLogin,
    refreshUser,
    signup,
    ssoAuthLogin,
    switchOrganization,
    tokenLogin,
    verifyUserAccountProvider,
    verifyWorkosEmail,
  };

  return (
    <>
      <AuthContext.Provider value={value}>
        <GoogleOAuthProvider clientId={googleOAuthClientId}>
          <MsalProvider instance={msalInstance}>{children}</MsalProvider>
        </GoogleOAuthProvider>
      </AuthContext.Provider>
      <Backdrop open={showBackdrop} sx={{ zIndex: 5 }}>
        <CircularProgress />
      </Backdrop>
    </>
  );
}
